import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface RegisterState {
  // Register submission
  isLoading: boolean;
  error?: string | null;
  emailConflict: boolean; // backend 409 on duplicate email
  success: boolean;

  // Email verification
  isVerifying: boolean;
  verifyError?: string | null;
  verified: boolean;

  // Resend verification code
  isResending: boolean;
  resendError?: string | null;
  lastResendAt?: number; // epoch ms; UI derives cooldown

  // Correct a mistyped address from the verify screen
  isChangingEmail: boolean;
}

export interface VerificationEmailChangedPayload {
  email: string;
  organizationEmailUpdated: boolean;
  /** When the fresh code left — restarts the resend cooldown. */
  sentAt: number;
}

const initialState: RegisterState = {
  isLoading: false,
  error: null,
  emailConflict: false,
  success: false,

  isVerifying: false,
  verifyError: null,
  verified: false,

  isResending: false,
  resendError: null,
  lastResendAt: undefined,

  isChangingEmail: false,
};

export const registerSlice = createSlice({
  name: "register",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoading = action.payload.isLoading;
    },
    setError: (state, action: PayloadAction<{ error?: string | null }>) => {
      state.error = action.payload.error;
    },
    setEmailConflict: (state, action: PayloadAction<{ emailConflict: boolean }>) => {
      state.emailConflict = action.payload.emailConflict;
    },
    setSuccess: (state, action: PayloadAction<{ success: boolean }>) => {
      state.success = action.payload.success;
    },
    clearError: (state) => {
      state.error = null;
      state.emailConflict = false;
    },

    setVerifying: (state, action: PayloadAction<{ isVerifying: boolean }>) => {
      state.isVerifying = action.payload.isVerifying;
    },
    setVerifyError: (state, action: PayloadAction<{ verifyError?: string | null }>) => {
      state.verifyError = action.payload.verifyError;
    },
    setVerified: (state, action: PayloadAction<{ verified: boolean }>) => {
      state.verified = action.payload.verified;
    },
    clearVerifyError: (state) => {
      state.verifyError = null;
    },

    setResending: (state, action: PayloadAction<{ isResending: boolean }>) => {
      state.isResending = action.payload.isResending;
    },
    setResendError: (state, action: PayloadAction<{ resendError?: string | null }>) => {
      state.resendError = action.payload.resendError;
    },
    setLastResendAt: (state, action: PayloadAction<{ lastResendAt?: number }>) => {
      state.lastResendAt = action.payload.lastResendAt;
    },
    clearResendError: (state) => {
      state.resendError = null;
    },

    setChangingEmail: (state, action: PayloadAction<{ isChangingEmail: boolean }>) => {
      state.isChangingEmail = action.payload.isChangingEmail;
    },
    /**
     * One action for the whole outcome, handled by `authSlice` too (it moves `user.email`): the verify form
     * is keyed by the address, so it must see the new address and the new cooldown in the same render — two
     * dispatches could let the old form read the cooldown restart as a plain resend and toast it.
     */
    verificationEmailChanged: (state, action: PayloadAction<VerificationEmailChangedPayload>) => {
      state.lastResendAt = action.payload.sentAt;
      state.verifyError = null;
    },

    reset: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setEmailConflict,
  setSuccess,
  clearError,
  setVerifying,
  setVerifyError,
  setVerified,
  clearVerifyError,
  setResending,
  setResendError,
  setLastResendAt,
  clearResendError,
  setChangingEmail,
  verificationEmailChanged,
  reset,
} = registerSlice.actions;
